import type { CompiledChapter, JourneyMode } from "@/data/schema/compiled";
import { buildBookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { snapshotBoardInstant } from "../BoardSample/instantSnapshot";
import { collectKeyframeTimesMs } from "../MarkerTrack/keyframeTimes";
import type { ChapterBoard } from "../ChapterBoard/types";
import { BOARD_REFERENCE_VIEWPORT_WIDTH } from "../ChapterBoard/types";
import { matchingRailRun } from "../../location/LocationRun";
import {
  characterGroupIds,
  emptyCharacterMarkers,
  emptyLocationGroups,
  ensurePartMarker,
  locationRuns,
  referenceLayoutView,
} from "../ChapterBoard/entities";
import { stayAt } from "../../rail/StayAt/stay";
import { HIDDEN_POSE, pushKeyframe } from "../MarkerTrack/track";

const VIEW_W = BOARD_REFERENCE_VIEWPORT_WIDTH;

const PRESENT_POSE = { ...HIDDEN_POSE, opacity: 1, visible: true, scale: 1, nameOpacity: 1 };

export function buildChapterBoard(
  chapter: CompiledChapter,
  journey: JourneyMode = "book",
): ChapterBoard {
  const layout = buildBookTimelineLayout(chapter, journey);
  const runs = locationRuns(layout);
  const times = collectKeyframeTimesMs(layout);
  const groupByCharacter = characterGroupIds(layout);
  const locationGroups = emptyLocationGroups(runs);
  const characterMarkers = emptyCharacterMarkers(layout, groupByCharacter);
  const partIndex = new Map();
  const groupByRun = new Map(locationGroups.map((group) => [group.branchId, group]));
  const factsByRun = new Map<string, { lane: number; runIndex: number }>();
  const runFacts = (runId: string): { lane: number; runIndex: number } => {
    const known = factsByRun.get(runId);
    if (known !== undefined) {
      return known;
    }
    const runIndex = runs.findIndex((run) => run.runId === runId);
    const sceneRun = runs[runIndex];
    const lane = sceneRun === undefined
      ? 0
      : (matchingRailRun(sceneRun, layout.rail.branches, layout.beats).lane
        ?? sceneRun.lane
        ?? 0);
    const facts = { lane, runIndex };
    factsByRun.set(runId, facts);
    return facts;
  };

  for (const ms of times) {
    const beat = layout.beats.find((b) => ms >= b.startMs && ms < b.endMs);
    const viewAt = referenceLayoutView(beat?.characterIds.length ?? 2);
    const snap = snapshotBoardInstant(layout, ms, VIEW_W, viewAt);
    // Sampling takes seats from the rail live, so a track only records when someone is here.
    for (const marker of characterMarkers) {
      const here = stayAt(layout.rail.stays, marker.characterId, ms) !== undefined;
      pushKeyframe(marker.track, ms, here ? PRESENT_POSE : HIDDEN_POSE);
    }
    for (const loc of snap.locations) {
      const group = groupByRun.get(loc.branchId);
      if (group === undefined) {
        continue;
      }
      const { lane, runIndex } = runFacts(loc.branchId);
      pushKeyframe(group.site.track, ms, {
        x: loc.anchorX + loc.siteDx,
        y: loc.topY,
        scale: 1,
        opacity: 1,
        visible: true,
        behind: false,
        nameOpacity: 1,
        stackAbove: lane < 0,
        layerZ: runIndex >= 0 ? runIndex : 0,
      });
      for (const part of loc.parts) {
        const partMarker = ensurePartMarker(group, part, partIndex);
        pushKeyframe(partMarker.track, ms, {
          x: loc.anchorX,
          y: loc.topY,
          partDx: part.dx,
          scale: 1,
          opacity: 1,
          visible: true,
          behind: false,
          nameOpacity: 1,
        });
      }
    }
  }

  return {
    bookId: chapter.bookId,
    chapterId: chapter.chapterId,
    title: chapter.title,
    totalDurationMs: layout.totalDurationMs,
    beats: layout.beats,
    rail: layout.rail,
    cast: layout.markers.map((marker) => ({
      characterId: marker.characterId,
      displayName: marker.displayName,
      imageUrl: marker.imageUrl,
      appearances: marker.appearances,
    })),
    characterMarkers,
    characterGroups: [...groupByCharacter.values()],
    locationGroups,
    referenceViewportWidth: VIEW_W,
  };
}
